/**
 * White-label brand substrate (docs/17-white-label-branding.md B1).
 *
 * A "brand" is data: a JSON document of token values that mirrors the
 * `@theme` custom properties in src/index.css. The compiled-in CSS *is* the
 * Steward default brand, so applying `null` simply removes the override
 * style element and the stylesheet's own values show through.
 *
 * Brand ≠ mode: light/dark/system stays the user's choice (ThemeProvider,
 * untouched) *within* whatever brand applies — a brand ships both palettes
 * and may restrict `modes` (e.g. a firm that only supplies a light palette).
 */

export type BrandMode = 'light' | 'dark' | 'system';

/**
 * The palette keys the brand substrate emits, as a runtime list so `cssBlock` can allowlist
 * them (#549): a TypeScript interface is erased at build and cannot filter anything.
 */
export const BRAND_PALETTE_KEYS = [
  'navy', 'cream', 'surface', 'bronze', 'slate', 'body', 'mid', 'danger', 'tag', 'ink',
] as const;

/** Keys are `--color-*` token names from src/index.css. */
export type BrandPalette = Record<(typeof BRAND_PALETTE_KEYS)[number], string>;

export interface BrandFonts {
  serif?: string;
  sans?: string;
  display?: string;
  /** Optional <link> swap; must be a fonts.googleapis.com URL (allowlisted). */
  stylesheetUrl?: string;
}

export interface BrandDocument {
  brandVersion: number;
  /** Replaces "Steward" in UI copy and titles. */
  productName: string;
  /** Text wordmark for the sidebar/login lockup; `text` also names the assistant ("Ask <text>"). */
  wordmark?: { text?: string; kicker?: string };
  /** Image URL, rendered by `BrandLockup` via `<img src={...}>`. WARNING for future
   *  implementers: render these ONLY via `<img>` (or CSS `background-image`), never
   *  `dangerouslySetInnerHTML`/inline `<svg>` — a stored SVG can carry
   *  `<script>`/event-handler payloads, and this value round-trips through
   *  operator-authored brand docs. `<img>` sandboxes SVG execution by design;
   *  inlining it would reopen that as stored XSS.
   *
   *  Naming convention: `logoLight`/`logoDark` name the BACKGROUND the asset is
   *  meant to sit on, not the ink colour of the mark itself — so a white wordmark
   *  (reads on a dark surface) is `logoDark`, and a navy wordmark (reads on a
   *  light surface) is `logoLight`. This mirrors how `palette.light`/`.dark` are
   *  already named after the surface, not the pixels. Either may be omitted (a
   *  firm that only supplies one variant); `BrandLockup` falls back to the text
   *  lockup for the theme mode with no matching asset — that is expected, not
   *  a bug, and must never render a broken-image icon.
   *
   *  ⚠️ CSP: `img-src` is `'self' data: https://cdn.plaid.com` and the CSP is
   *  ENFORCING in every environment (deploy/lib/csp-mode.ts). Prefer a `'self'`
   *  path (self-hosted under `public/`, like the RIPL preset) over a `data:` URI
   *  or a remote host: `data:` round-trips through `localStorage` (BRAND_DOC_CACHE_KEY)
   *  and a kilobyte-scale logo bloats that cache for every branded session, and an
   *  https URL on any OTHER origin is BLOCKED outright — including the documents
   *  CDN or a marketing site, the two most natural places an operator would first
   *  reach for. A server-seeded brand doc (appcore) that wants this to work for an
   *  arbitrary operator-supplied logo (not just compiled-in presets like RIPL's)
   *  will need either an upload path that lands the file under this app's own
   *  origin, or a widened `img-src` — neither exists yet; this field only renders
   *  today for `'self'`-hosted assets. Loading must fail SOFT (`onError` → text
   *  lockup) rather than a broken-image icon. */
  logoLight?: string;
  logoDark?: string;
  favicon?: string;
  fonts?: BrandFonts;
  palette: { light: BrandPalette; dark?: BrandPalette };
  /** Which theme modes the brand permits; omitted = all three. */
  modes?: BrandMode[];
  /** Scenario 1 (firm white-label): users cannot override with presets. */
  locked?: boolean;
}

/** Defaults when no brand applies (the compiled-in Steward identity). */
export const DEFAULT_PRODUCT_NAME = 'Steward';
export const DEFAULT_KICKER = 'Crossmarc';

const STYLE_ID = 'brand-tokens';
const FONT_LINK_ID = 'brand-fonts';

// localStorage keys — read by the index.html boot script for flash-free first paint.
export const BRAND_CSS_CACHE_KEY = 'brand-css';
export const BRAND_FONTS_CACHE_KEY = 'brand-fonts-href';
export const BRAND_DOC_CACHE_KEY = 'brand-doc';
// The hostname the cached brand was resolved for (B3). The flash cache is a
// per-host guess: a shared browser that visited firm A must NOT flash A's brand
// when it later lands on firm B's subdomain, so the cache is only honored when its
// host tag matches the current hostname. Written on every applyBrand; the
// index.html boot script performs the same host match before restoring the CSS.
export const BRAND_HOST_CACHE_KEY = 'brand-host';

/** The current page hostname (empty in non-DOM contexts). */
function currentHostname(): string {
  return typeof window !== 'undefined' && window.location ? window.location.hostname : '';
}

const FONT_ALLOWLIST_PREFIX = 'https://fonts.googleapis.com/';

function cssBlock(selector: string, palette: BrandPalette, fonts?: BrandFonts): string {
  const lines: string[] = [];
  for (const [key, value] of Object.entries(palette)) {
    // Values come from a Zod-validated server doc or a compiled-in preset, but
    // re-filter here since this string is injected into a <style> element. The KEY is
    // filtered too (#549): only a known token name is emitted, so a key carrying `}` or
    // `;` cannot close the rule and add its own CSS.
    if (!(BRAND_PALETTE_KEYS as readonly string[]).includes(key)) continue;
    if (/^#[0-9a-fA-F]{6}$/.test(value)) lines.push(`--color-${key}: ${value};`);
  }
  if (fonts?.serif) lines.push(`--font-serif: ${sanitizeFontStack(fonts.serif)};`);
  if (fonts?.sans) lines.push(`--font-sans: ${sanitizeFontStack(fonts.sans)};`);
  if (fonts?.display) lines.push(`--font-display: ${sanitizeFontStack(fonts.display)};`);
  return `${selector} {\n  ${lines.join('\n  ')}\n}`;
}

function sanitizeFontStack(stack: string): string {
  // Font stacks are names, quotes, commas — never braces/semicolons/urls.
  return stack.replace(/[;{}<>]/g, '').slice(0, 200);
}

/**
 * Compile a brand document to the CSS override block injected at :root.
 *
 * Selectors are deliberately `:root:root` / `:root[data-theme='dark']` rather
 * than the more obvious `:root` / `[data-theme='dark']` — both compile to the
 * same 0,2,0 specificity (a repeated simple selector still counts twice), one
 * notch above src/index.css's own unlayered `[data-theme='dark']` block
 * (0,1,0). Without that bump, document order decides between two equal-
 * specificity unlayered rules, and `public/boot.js` inserts this stylesheet
 * BEFORE the app's own <link>, so the app's dark tokens would win on every
 * cached load (#708). Do not simplify these back to single-`:root` selectors.
 */
export function brandCss(doc: BrandDocument): string {
  const parts = [cssBlock(':root:root', doc.palette.light, doc.fonts)];
  if (doc.palette.dark) {
    parts.push(cssBlock(":root[data-theme='dark']", doc.palette.dark, doc.fonts));
  }
  return parts.join('\n');
}

function upsertStyle(css: string | null) {
  let el = document.getElementById(STYLE_ID);
  if (!css) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('style');
    el.id = STYLE_ID;
  }
  el.textContent = css;
  // Belt-and-braces (#708): always (re)append, even for an existing element —
  // `appendChild` on a node already in the document MOVES it rather than
  // cloning it, so this pushes brand-tokens to the end of <head> on every
  // call. This alone only fixes the post-mount (React) ordering window; it
  // cannot fix boot.js's pre-paint window, since boot.js runs before this
  // module's JS is even loaded. The `:root:root` specificity bump above is
  // what actually closes that window. Keep both: specificity makes position
  // stop mattering, this keeps position correct anyway. Do not remove this
  // believing the specificity fix alone makes it redundant.
  document.head.appendChild(el);
}

function upsertFontLink(href: string | null) {
  let el = document.getElementById(FONT_LINK_ID) as HTMLLinkElement | null;
  if (!href || !href.startsWith(FONT_ALLOWLIST_PREFIX)) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('link');
    el.id = FONT_LINK_ID;
    el.rel = 'stylesheet';
    document.head.appendChild(el);
  }
  if (el.href !== href) el.href = href;
}

function swapFavicon(href: string | null) {
  const el = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!el) return;
  if (href && href.startsWith('data:image/')) {
    if (!el.dataset['defaultHref']) el.dataset['defaultHref'] = el.href;
    el.href = href;
  } else if (el.dataset['defaultHref']) {
    el.href = el.dataset['defaultHref'];
  }
}

function writeCache(key: string, value: string | null) {
  try {
    if (value == null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // storage failures (private browsing) only cost the flash-free first paint
  }
}

/**
 * Apply (or clear, with `null`) a brand: token style element, font stylesheet,
 * favicon — and (when `persist`, the default) cache the compiled artifacts so the
 * index.html boot script can restore them before the bundle loads on the next visit.
 *
 * `persist: false` injects the brand for the current session only, leaving the
 * flash cache untouched — used for the `?brandHost=` demo override (honored in
 * dev/stage/local only; prod refuses it outright, see `resolveBrandHost` in
 * BrandProvider), so previewing firm A's brand from the shared host can never poison
 * the persistent, host-keyed cache (which would otherwise flash A on a later plain
 * visit to that host).
 */
export function applyBrand(doc: BrandDocument | null, persist = true) {
  if (!doc) {
    upsertStyle(null);
    upsertFontLink(null);
    swapFavicon(null);
    if (persist) clearBrandCache();
    return;
  }
  const css = brandCss(doc);
  const fontsHref = doc.fonts?.stylesheetUrl && doc.fonts.stylesheetUrl.startsWith(FONT_ALLOWLIST_PREFIX)
    ? doc.fonts.stylesheetUrl
    : null;
  upsertStyle(css);
  upsertFontLink(fontsHref);
  swapFavicon(doc.favicon ?? null);
  if (!persist) return;
  writeCache(BRAND_CSS_CACHE_KEY, css);
  writeCache(BRAND_FONTS_CACHE_KEY, fontsHref);
  writeCache(BRAND_DOC_CACHE_KEY, JSON.stringify(doc));
  // Tag the cache with the host it was resolved for, so the next pre-auth first
  // paint only restores it on the SAME host (no cross-firm flash, B3).
  writeCache(BRAND_HOST_CACHE_KEY, currentHostname());
}

/** Clear the cached brand artifacts (so a subsequent `readCachedBrand()` returns
 *  null). Reached through `applyBrand(null, persist)`: once the resolver has
 *  answered "no brand for this host", a doc cached by an earlier visit must not
 *  resurrect itself on the next pre-auth paint. (It also used to back the
 *  `?brand=off` preview switch, retired in steward-app#664.) */
export function clearBrandCache() {
  writeCache(BRAND_CSS_CACHE_KEY, null);
  writeCache(BRAND_FONTS_CACHE_KEY, null);
  writeCache(BRAND_DOC_CACHE_KEY, null);
  writeCache(BRAND_HOST_CACHE_KEY, null);
}

/**
 * The brand cached by the last visit (used pre-auth, before /me answers). Honored
 * only when its host tag matches the current hostname (B3) — otherwise a browser
 * that cached firm A's brand would flash it on firm B's subdomain. An untagged
 * legacy cache is treated as a mismatch (no branded flash, which for a Steward-
 * default user is invisible anyway).
 */
export function readCachedBrand(): BrandDocument | null {
  try {
    if (localStorage.getItem(BRAND_HOST_CACHE_KEY) !== currentHostname()) return null;
    const raw = localStorage.getItem(BRAND_DOC_CACHE_KEY);
    if (!raw) return null;
    const doc = JSON.parse(raw) as BrandDocument;
    return doc && typeof doc.productName === 'string' && doc.palette?.light ? doc : null;
  } catch {
    return null;
  }
}

/** Shape-validates a brand document received from the host resolver (defense in
 *  depth — the SPA re-checks even a server-sourced doc before injecting it). */
export function isValidBrandDoc(doc: unknown): doc is BrandDocument {
  const d = doc as BrandDocument | null;
  return !!d && typeof d.productName === 'string' && !!d.palette?.light;
}
